import React, { useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState("");

  // Reset form when dialog opens/closes
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
    }
    onOpenChange(isOpen);
  };

  // Password strength check
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: "", color: "" };
    
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { level: 1, text: "Weak", color: "text-red-400" };
    if (score <= 4) return { level: 2, text: "Medium", color: "text-amber-400" };
    return { level: 3, text: "Strong", color: "text-emerald-400" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validations
    if (!currentPassword) {
      setError("Current password is required");
      return;
    }
    if (!newPassword) {
      setError("New password is required");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }
    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      toast({ title: "Password changed successfully" });
      handleOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to change password";
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md"
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-indigo-400" />
            Change Password
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-rose-500/10 border border-rose-500/20 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Current Password */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">Current Password</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10 pr-10"
                placeholder="Enter your current password"
                data-testid="change-password-current"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">New Password</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10 pr-10"
                placeholder="Enter your new password"
                data-testid="change-password-new"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPassword && (
              <div className="flex items-center gap-2 text-xs">
                <div className="flex gap-1">
                  {[1, 2, 3].map((level) => (
                    <div
                      key={level}
                      className={`h-1 w-6 rounded-full ${
                        passwordStrength.level >= level
                          ? passwordStrength.level === 1
                            ? "bg-red-400"
                            : passwordStrength.level === 2
                            ? "bg-amber-400"
                            : "bg-emerald-400"
                          : "bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <span className={passwordStrength.color}>{passwordStrength.text}</span>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">Confirm New Password</Label>
            <div className="relative">
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`bg-white/5 ring-1 ring-white/10 pr-10 ${
                  confirmPassword && confirmPassword !== newPassword
                    ? "ring-rose-500/50"
                    : confirmPassword && confirmPassword === newPassword
                    ? "ring-emerald-500/50"
                    : ""
                }`}
                placeholder="Confirm your new password"
                data-testid="change-password-confirm"
              />
              {confirmPassword && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {confirmPassword === newPassword ? (
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-rose-400" />
                  )}
                </div>
              )}
            </div>
            {confirmPassword && confirmPassword !== newPassword && (
              <p className="text-xs text-rose-400">Passwords do not match</p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="rounded-lg bg-white/5 p-3 text-xs text-slate-400">
            <p className="font-medium text-slate-300 mb-1">Password requirements:</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>At least 6 characters</li>
              <li>Mix of uppercase, lowercase, and numbers recommended</li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleOpenChange(false)}
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !currentPassword || !newPassword || newPassword !== confirmPassword}
              data-testid="change-password-submit"
            >
              {saving ? "Saving…" : "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
